import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Menu } from 'antd';
import type { MenuProps } from 'antd';
import type { ManagerDetailResponseTreeItem } from '@/apis';
import { renderIcon } from '@/utils/iconRenderer';

type MenuItem = Required<MenuProps>['items'][number];

// Define props type
interface MenuComponentProps {
  permissionTree: ManagerDetailResponseTreeItem[];
}

const MenuComponent: React.FC<MenuComponentProps> = props => {
  const navigate = useNavigate();
  const location = useLocation();
  const [openKeys, setOpenKeys] = useState<string[]>([]);
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const [menus, setMenus] = useState<MenuItem[]>([]);

  function getItem(
    label: React.ReactNode,
    key: React.Key,
    icon?: React.ReactNode,
    children?: MenuItem[],
    type?: 'group'
  ): MenuItem {
    return {
      key,
      icon,
      children,
      label,
      type,
    } as MenuItem;
  }

  /** 递归判断节点或其任意后代是否存在「系统菜单」(permissionType == 1，兼容数字与字符串) */
  const hasMenuDescendant = useCallback(
    (node: ManagerDetailResponseTreeItem): boolean => {
      if (Number(node.permissionType) === 1) return true;
      const children = node.children ?? [];
      if (Array.isArray(children) && children.length > 0) {
        return children.some(child => hasMenuDescendant(child));
      }
      return false;
    },
    []
  );

  const renderMenus = useCallback(
    (data: ManagerDetailResponseTreeItem[]): MenuItem[] => {
      if (!Array.isArray(data) || data.length === 0) return [];
      return data
        .map(item => {
          const children = item.children ?? [];
          const hasChildren = Array.isArray(children) && children.length > 0;
          const hasMenuChild =
            hasChildren && children.some(child => hasMenuDescendant(child));
          if (hasMenuChild) {
            return getItem(
              item.permissionName,
              item.permissionKey,
              renderIcon(item.icon),
              renderMenus(children)
            );
          }
          if (Number(item.permissionType) === 1) {
            return getItem(
              item.permissionName,
              item.permissionKey,
              renderIcon(item.icon)
            );
          }
          return null;
        })
        .filter((item): item is MenuItem => item !== null);
    },
    [hasMenuDescendant]
  );

  // Flattens the permission tree into an array
  const toArray = useCallback(
    (
      list: ManagerDetailResponseTreeItem[],
      data: ManagerDetailResponseTreeItem[] = []
    ): ManagerDetailResponseTreeItem[] => {
      [...list].forEach(item => {
        const { children, ...rest } = item;
        data.push(rest as ManagerDetailResponseTreeItem);

        if (Array.isArray(children)) {
          toArray(children, data);
        }
      });
      return data;
    },
    []
  );

  useEffect(() => {
    if (Array.isArray(props.permissionTree)) {
      setMenus(renderMenus(props.permissionTree));
    }
  }, [props.permissionTree, renderMenus]);

  // 根据当前路由设置选中的菜单项和展开的菜单
  useEffect(() => {
    if (
      Array.isArray(props.permissionTree) &&
      props.permissionTree.length > 0
    ) {
      const flatPermissions = toArray(props.permissionTree);
      const currentPath = location.pathname;

      // 查找匹配当前路径的菜单项
      const matchedMenu = flatPermissions.find(
        menu =>
          Number(menu.permissionType) === 1 &&
          menu.permissionMark === currentPath
      );

      if (matchedMenu) {
        setSelectedKeys([matchedMenu.permissionKey]);

        // 查找父级菜单并展开
        const findParentKeys = (
          items: ManagerDetailResponseTreeItem[],
          targetKey: string,
          parentKeys: string[] = []
        ): string[] => {
          for (const item of items) {
            if (item.permissionKey === targetKey) {
              return parentKeys;
            }
            if (item.children && item.children.length > 0) {
              const result = findParentKeys(item.children, targetKey, [
                ...parentKeys,
                item.permissionKey,
              ]);
              if (result.length > 0) {
                return result;
              }
            }
          }
          return [];
        };

        const parentKeys = findParentKeys(
          props.permissionTree,
          matchedMenu.permissionKey
        );
        setOpenKeys(parentKeys);
      }
    }
  }, [location.pathname, props.permissionTree, toArray]);

  // submenu keys of first level
  const rootSubmenuKeys = ['sub1', 'sub2', 'sub4'];
  const onOpenChange: MenuProps['onOpenChange'] = keys => {
    const latestOpenKey = keys.find(
      key => openKeys.indexOf(key as string) === -1
    );
    if (rootSubmenuKeys.indexOf(latestOpenKey as string) === -1) {
      setOpenKeys(keys as string[]);
    } else {
      setOpenKeys(latestOpenKey ? [latestOpenKey as string] : []);
    }
  };

  const handleClick: MenuProps['onClick'] = e => {
    const flatPermissions = toArray(props.permissionTree || []);
    const menu = flatPermissions.find(menu => {
      return menu.permissionKey == e.key;
    });
    if (Number(menu?.permissionType) === 1 && menu?.permissionMark !== '-') {
      setSelectedKeys([e.key as string]);
      navigate(menu?.permissionMark ?? '');
    }
  };

  return (
    <Menu
      onClick={handleClick}
      style={{ width: 256 }}
      selectedKeys={selectedKeys}
      mode='inline'
      openKeys={openKeys}
      onOpenChange={onOpenChange}
      items={menus}
    />
  );
};

export default MenuComponent;
